import React, { useState, useEffect } from 'react'
import { useConfigStore } from '@renderer/stores/configState'
import { useTranslation } from 'react-i18next'
import Button from '@renderer/components/Button'

interface FrameSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  selectedFrames: string[]
  onFramesSelected: (frameIds: string[]) => void
  printerType?: 'secondary' | 'tertiary'
}

export const FrameSelectionModal: React.FC<FrameSelectionModalProps> = ({
  isOpen,
  onClose,
  selectedFrames,
  onFramesSelected,
  printerType = 'secondary'
}) => {
  const { t } = useTranslation()
  const { frames } = useConfigStore()
  const [tempSelectedFrames, setTempSelectedFrames] = useState<string[]>(selectedFrames)

  // Critical: Update tempSelectedFrames when selectedFrames or printerType changes
  // This ensures the modal shows the correct frames when switching between secondary and tertiary
  useEffect(() => {
    setTempSelectedFrames([...selectedFrames])
  }, [selectedFrames, printerType, isOpen])

  if (!isOpen) return null

  const handleFrameToggle = (frameId: string): void => {
    setTempSelectedFrames((prev) =>
      prev.includes(frameId) ? prev.filter((id) => id !== frameId) : [...prev, frameId]
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold">
            {t(
              printerType === 'tertiary'
                ? 'printerSettings.selectFramesTertiary'
                : 'printerSettings.selectFrames',
              printerType === 'tertiary'
                ? 'Select Frames for Tertiary Printer'
                : 'Select Frames for Secondary Printer'
            )}
          </h2>
          <p className="text-gray-600 mt-2">
            {t(
              printerType === 'tertiary'
                ? 'printerSettings.selectFramesDescriptionTertiary'
                : 'printerSettings.selectFramesDescription',
              printerType === 'tertiary'
                ? 'Choose which frames should be printed using the tertiary printer'
                : 'Choose which frames should be printed using the secondary printer'
            )}
          </p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            {frames.map((frame) => (
              <div
                key={frame._id}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  tempSelectedFrames.includes(frame._id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => handleFrameToggle(frame._id)}
              >
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={tempSelectedFrames.includes(frame._id)}
                    onChange={() => handleFrameToggle(frame._id)}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    title={t(
                      printerType === 'tertiary'
                        ? 'printerSettings.selectFrameTertiary'
                        : 'printerSettings.selectFrame',
                      printerType === 'tertiary'
                        ? 'Select frame for tertiary printer'
                        : 'Select frame for secondary printer'
                    )}
                  />
                  <img
                    src={frame.thumbnail}
                    alt={frame.name}
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div>
                    <h3 className="font-medium">{frame.name}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <Button
            text={t('common.cancel', 'Cancel')}
            variant="secondary"
            onClick={onClose}
            className="!px-6 !py-2 !text-base"
          />
          <Button
            text={t('common.save', 'Save')}
            onClick={() => {
              onFramesSelected(tempSelectedFrames)
              onClose()
            }}
            className="!px-6 !py-2 !text-base"
          />
        </div>
      </div>
    </div>
  )
}
